/**
 * Emails the studio sends to a client, as recorded by the existing tables:
 * tasting_thanks (menu choice), send_outbox (proposal), tasting_guest_lists, tasting_contracts.
 * Nothing here sends anything; it only reads what was stored.
 */

export type SentEmailKind = "thanks" | "proposal" | "guests" | "contract";

/** sent: handed to the provider. scheduled: waiting for its time. late: its time passed without a send. failed/uncertain/pending/captured come from the proposal outbox. */
export type SentEmailState = "sent" | "scheduled" | "late" | "failed" | "uncertain" | "pending" | "captured";

export type SentEmail = {
  kind: SentEmailKind;
  step: 1 | 2 | 3 | 4;
  state: SentEmailState;
  /** ISO time of the send, or of the planned send when scheduled/late. */
  at: string;
  recipients: string[];
  /** Short extra fact, e.g. "υπογράφηκε 31/10 10:02" or "επιλογή: Social". */
  note: string | null;
};

export const SENT_EMAIL_LABELS: Record<SentEmailKind, string> = {
  thanks: "Email επιλογής μενού",
  proposal: "Πρόταση",
  guests: "Email οριστικοποίησης καλεσμένων",
  contract: "Τελικό email υπογραφής (συμφωνητικό)",
};

export const SENT_EMAIL_STEPS: Record<SentEmailKind, 1 | 2 | 3 | 4> = { thanks: 3, proposal: 3, guests: 4, contract: 4 };

export const SENT_EMAIL_STATE_LABELS: Record<SentEmailState, string> = {
  sent: "στάλθηκε",
  scheduled: "προγραμματισμένο",
  late: "δεν έφυγε ακόμα",
  failed: "απέτυχε",
  uncertain: "αβέβαιη αποστολή",
  pending: "σε αποστολή",
  captured: "δεν στάλθηκε (μόνο τοπική καταγραφή)",
};

/** A scheduled thank-you counts as late once its due time is this far in the past. */
const LATE_AFTER_MS = 15 * 60 * 1000;

export function thanksEmailState(sentAt: string | null, dueAt: string, now = Date.now()): SentEmailState {
  if (sentAt) return "sent";
  const due = Date.parse(dueAt);
  return Number.isFinite(due) && now - due > LATE_AFTER_MS ? "late" : "scheduled";
}

/** Maps a send_outbox status to a client-facing state. */
export function outboxEmailState(status: string): SentEmailState {
  if (status === "submitted" || status === "sent" || status === "delivered") return "sent";
  if (status === "failed") return "failed";
  if (status === "uncertain") return "uncertain";
  if (status === "captured") return "captured";
  return "pending";
}

/** "30/10 09:15" in Europe/Athens. */
export function athensStamp(value: string | null | undefined, timeZone = "Europe/Athens"): string {
  const date = new Date(value ?? "");
  if (Number.isNaN(date.getTime())) return value ?? "";
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(date);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "00";
  const hour = part("hour") === "24" ? "00" : part("hour");
  return `${part("day")}/${part("month")} ${hour}:${part("minute")}`;
}

/** "30/10/2026 09:15" in Europe/Athens, for the list. */
export function athensStampLong(value: string | null | undefined, timeZone = "Europe/Athens"): string {
  const date = new Date(value ?? "");
  if (Number.isNaN(date.getTime())) return value ?? "";
  const year = new Intl.DateTimeFormat("en-GB", { timeZone, year: "numeric" }).format(date);
  const [day, time] = athensStamp(value, timeZone).split(" ");
  return `${day}/${year} ${time}`;
}

export function parseRecipients(value: string | null | undefined): string[] {
  try {
    const parsed: unknown = JSON.parse(value ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string" && item.trim() !== "") : [];
  } catch {
    return [];
  }
}

/** Newest first; scheduled items sort by their planned time. */
export function sortSentEmails(items: SentEmail[]): SentEmail[] {
  return [...items].sort((a, b) => b.at.localeCompare(a.at));
}

/** The marker next to a step pill: the newest email for that step, e.g. "✉ στάλθηκε 30/10 09:15". */
export function stepEmailBadge(items: SentEmail[], step: number): string | null {
  const own = sortSentEmails(items.filter((item) => item.step === step));
  const shown = own.find((item) => item.state === "sent") ?? own[0];
  if (!shown) return null;
  return `✉ ${SENT_EMAIL_STATE_LABELS[shown.state]} ${athensStamp(shown.at)}`;
}
